import { ShieldAlert, ShieldCheck } from 'lucide-react';
import { useInstanceReconciliation } from '../hooks/useOperator';
import { desktopDeepLinks } from '../lib/deep-links';
import { DeepLinkButton, Field } from './operator-ui';

/**
 * WI-14 (OPERATOR-UI-PLACEMENT): read-only reconciliation view for a selected
 * attempt. `requiresReconciliation` is true only for the terminal
 * OUTCOME_UNKNOWN state (EFFECT-GUARANTEE) — the console flags it and deep-links
 * the operator to the native reconciliation surface; it never resolves here.
 */
export default function ReconciliationPanel({ instanceId }: { instanceId: string }) {
  const { data, isLoading } = useInstanceReconciliation(instanceId);

  if (isLoading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-400">
        Loading reconciliation…
      </div>
    );
  }

  if (!data) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-400">
        No reconciliation state
      </div>
    );
  }

  const flagged = data.requiresReconciliation;

  return (
    <div
      className={`rounded-xl border bg-white p-6 ${
        flagged ? 'border-amber-300' : 'border-slate-200'
      }`}
    >
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {flagged ? (
            <ShieldAlert className="h-4 w-4 text-amber-500" />
          ) : (
            <ShieldCheck className="h-4 w-4 text-emerald-500" />
          )}
          <h2 className="text-sm font-semibold text-slate-900">Reconciliation</h2>
        </div>
        {flagged && (
          <DeepLinkButton
            href={desktopDeepLinks.reconciliation(instanceId)}
            label="Reconcile in Desktop"
          />
        )}
      </div>

      {flagged ? (
        <div className="mb-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">
          Terminal outcome is <span className="font-mono">OUTCOME_UNKNOWN</span>. Operator
          reconciliation is required to establish the effect guarantee.
        </div>
      ) : (
        <div className="mb-4 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          No reconciliation required. Current status:{' '}
          <span className="font-mono">{data.status}</span>.
        </div>
      )}

      <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <Field label="Status" value={data.status} mono />
        <Field label="Attempt" value={data.attempt} />
        <Field label="Generation" value={data.generation} />
        <Field label="Run group" value={data.runGroupId} mono />
        <Field label="Execution request" value={data.executionRequestId} mono />
        <Field label="Idempotency key" value={data.idempotencyKey} mono />
        <Field label="Checkpoint ref" value={data.checkpointRef} mono />
      </dl>
    </div>
  );
}
